'use client';

import { useEffect, useRef, useState, type CSSProperties, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AccountError,
  useBlockPart,
  useCommerceSource,
  tx,
  PortalLink,
  type AccountAddress,
} from '@akademi/site-builder-runtime';
import { AdapterPlaceholder, BlockRoot, sharedStyles } from '@akademi/site-builder-blocks';
import {
  AccountField,
  buttonStyle,
  errorStyle,
  leadStyle,
  mutedStyle,
  noticeStyle,
  secondaryButtonStyle,
  signInHrefFor,
  titleStyle,
  useAccountErrorText,
  useSignInRedirect,
} from './accountShared';
import { addressLines, draftFrom, emptyDraft, inputFrom, type AddressDraft } from './addressDraft';

const { sectionInner } = sharedStyles;

type Localized = string | Record<string, string>;

/** The server's limit, repeated so the page can say so instead of offering a form it will refuse. */
export const MAX_SAVED_ADDRESSES = 20;

/**
 * The signed-in shopper's saved addresses: add, change, remove, choose the default.
 *
 * <p>The checkout offers these in a picker, so an address is typed once. Every
 * change answers with the whole book from the server, and the page shows that
 * answer rather than patching its own copy: choosing a default or removing one
 * changes other addresses too, and a list the browser worked out is a list that
 * can disagree with the one the checkout will read.
 */
export interface AddressBookProps {
  title?: Localized;
  intro?: Localized;
  accountHref?: string;
  signInHref?: string;
  /** ISO 3166-1 alpha-2 a new address starts on. */
  defaultCountryCode?: string;
}

type LoadState = 'loading' | 'ready' | 'signed-out' | 'failed';

export function AddressBook({
  title,
  intro,
  accountHref = '/account',
  signInHref = '/sign-in',
  defaultCountryCode = 'GB',
}: AddressBookProps) {
  const commerce = useCommerceSource();
  const part = useBlockPart();
  const { t } = useTranslation('site-builder-blocks');
  const errorText = useAccountErrorText();

  const [state, setState] = useState<LoadState>('loading');
  const [book, setBook] = useState<AccountAddress[]>([]);
  const [shopperName, setShopperName] = useState('');
  /** `'new'`, the id being changed, or null when no form is open. */
  const [editing, setEditing] = useState<'new' | number | null>(null);
  const [draft, setDraft] = useState<AddressDraft>(() => emptyDraft(defaultCountryCode));
  const [removing, setRemoving] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!commerce?.accountAddresses) return;
    const profile = commerce.accountProfile ? commerce.accountProfile() : Promise.resolve(undefined);
    Promise.all([profile, commerce.accountAddresses()])
      .then(([found, addresses]) => {
        if (cancelled) return;
        if (found === null) {
          setState('signed-out');
          return;
        }
        setShopperName([found?.firstName, found?.lastName].filter(Boolean).join(' '));
        setBook(addresses);
        setState('ready');
      })
      .catch((failure) => {
        if (cancelled) return;
        setState(isSignedOut(failure) ? 'signed-out' : 'failed');
      });
    return () => {
      cancelled = true;
    };
  }, [commerce]);

  const here =
    typeof window === 'undefined' ? '/account-addresses' : `${window.location.pathname}${window.location.search}`;
  useSignInRedirect(state === 'signed-out', signInHref, here);

  if (!commerce?.accountAddresses) {
    return (
      <BlockRoot as="section">
        <div style={sectionInner}>
          <AdapterPlaceholder
            blockType="AddressBook"
            adapterName="CommerceSource"
            reason={t('commerce.account.noAdapter')}
          />
        </div>
      </BlockRoot>
    );
  }

  /** Runs one change and shows the book the server answers with. */
  async function change(run: () => Promise<AccountAddress[]> | undefined, done: string) {
    if (busy) return;
    const pending = run();
    if (!pending) return;
    setBusy(true);
    setNotice(null);
    setError(null);
    try {
      setBook(await pending);
      setEditing(null);
      setRemoving(null);
      setNotice(done);
    } catch (failure) {
      if (isSignedOut(failure)) {
        setState('signed-out');
      } else if (failure instanceof AccountError && failure.kind === 'NOT_FOUND') {
        setError(t('commerce.addressBook.notFound'));
      } else {
        setError(errorText(failure));
      }
    } finally {
      setBusy(false);
    }
  }

  function openNew() {
    // The first address a shopper saves is most often their own.
    setDraft(emptyDraft(defaultCountryCode, book.length === 0 ? shopperName : ''));
    setEditing('new');
    setRemoving(null);
    setNotice(null);
    setError(null);
  }

  function openEdit(address: AccountAddress) {
    setDraft({ ...draftFrom(address), makeDefault: false });
    setEditing(address.id);
    setRemoving(null);
    setNotice(null);
    setError(null);
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const input = inputFrom(draft);
    void change(
      () =>
        editing === 'new'
          ? commerce?.saveAccountAddress?.(input)
          : typeof editing === 'number'
            ? commerce?.updateAccountAddress?.(editing, input)
            : undefined,
      t('commerce.addressBook.saved'),
    );
  }

  const full = book.length >= MAX_SAVED_ADDRESSES;
  const nameOf = (address: AccountAddress) => address.label || address.line1;

  return (
    <BlockRoot as="section">
      <div style={{ ...sectionInner, maxWidth: 900 }}>
        <PortalLink href={accountHref} style={backStyle}>
          {t('commerce.account.backToAccount')}
        </PortalLink>
        <h1 {...part('title')} style={titleStyle}>
          {tx(title) || t('commerce.addressBook.title')}
        </h1>

        {state === 'loading' && <p style={mutedStyle}>{t('commerce.account.loading')}</p>}

        {state === 'failed' && (
          <p role="alert" style={errorStyle}>
            {t('commerce.account.errors.UNAVAILABLE')}
          </p>
        )}

        {state === 'signed-out' && (
          <p style={mutedStyle}>
            {t('commerce.account.signedOutRedirecting')}{' '}
            <PortalLink href={signInHrefFor(signInHref, here)}>
              {t('commerce.account.signIn')}
            </PortalLink>
          </p>
        )}

        {state === 'ready' && (
          <div style={{ display: 'grid', gap: 24 }}>
            <div style={{ display: 'grid', gap: 12 }}>
              <p {...part('intro')} style={{ ...leadStyle, margin: 0 }}>
                {tx(intro) || t('commerce.addressBook.intro')}
              </p>
              {/* Always present, so the "saved" and "removed" notices are announced when they appear. */}
              <div aria-live="polite">
                {notice && (
                  <p role="status" style={noticeStyle}>
                    {notice}
                  </p>
                )}
              </div>
              {error && (
                <p role="alert" style={errorStyle}>
                  {error}
                </p>
              )}
            </div>

            {book.length === 0 && editing === null && <p style={mutedStyle}>{t('commerce.addressBook.empty')}</p>}

            {book.length > 0 && (
              <ul {...part('list')} style={listStyle}>
                {book.map((address) => (
                  <li key={address.id} style={cardStyle}>
                    <div style={cardHeadStyle}>
                      <span style={{ fontWeight: 600, minWidth: 0, overflowWrap: 'anywhere' }}>
                        {address.label || t('commerce.addressBook.unlabelled')}
                      </span>
                      {address.defaultAddress && <span style={badgeStyle}>{t('commerce.addressBook.default')}</span>}
                    </div>
                    <address style={addressStyle}>
                      {addressLines(address).map((line, index) => (
                        <span key={index} style={{ display: 'block' }}>
                          {line}
                        </span>
                      ))}
                      {address.phone && <span style={{ display: 'block' }}>{address.phone}</span>}
                    </address>

                    {removing === address.id ? (
                      <div role="group" aria-label={t('commerce.addressBook.removeConfirm')} style={actionsStyle}>
                        <span style={{ fontSize: 14 }}>{t('commerce.addressBook.removeConfirm')}</span>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() =>
                            void change(
                              () => commerce?.removeAccountAddress?.(address.id),
                              t('commerce.addressBook.removed'),
                            )
                          }
                          style={dangerLinkStyle}
                        >
                          {t('commerce.addressBook.removeYes')}
                        </button>
                        <button type="button" onClick={() => setRemoving(null)} style={linkButtonStyle}>
                          {t('commerce.addressBook.keep')}
                        </button>
                      </div>
                    ) : (
                      <div style={actionsStyle}>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => openEdit(address)}
                          aria-label={t('commerce.addressBook.editAria', { name: nameOf(address) })}
                          style={linkButtonStyle}
                        >
                          {t('commerce.addressBook.edit')}
                        </button>
                        {!address.defaultAddress && commerce.makeDefaultAccountAddress && (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() =>
                              void change(
                                () => commerce?.makeDefaultAccountAddress?.(address.id),
                                t('commerce.addressBook.defaultChanged'),
                              )
                            }
                            aria-label={t('commerce.addressBook.setDefaultAria', { name: nameOf(address) })}
                            style={linkButtonStyle}
                          >
                            {t('commerce.addressBook.setDefault')}
                          </button>
                        )}
                        {commerce.removeAccountAddress && (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => {
                              setRemoving(address.id);
                              setEditing(null);
                            }}
                            aria-label={t('commerce.addressBook.removeAria', { name: nameOf(address) })}
                            style={linkButtonStyle}
                          >
                            {t('commerce.addressBook.remove')}
                          </button>
                        )}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {editing !== null ? (
              <AddressForm
                draft={draft}
                onChange={setDraft}
                heading={
                  editing === 'new' ? t('commerce.addressBook.addTitle') : t('commerce.addressBook.editTitle')
                }
                busy={busy}
                // A shopper with one address already has it as the default; the box only means something after that.
                offerDefault={book.length > 0 && !(typeof editing === 'number' && isDefault(book, editing))}
                onSubmit={submit}
                onCancel={() => {
                  setEditing(null);
                  setError(null);
                }}
              />
            ) : full ? (
              <p style={mutedStyle}>{t('commerce.addressBook.full')}</p>
            ) : (
              commerce.saveAccountAddress && (
                <button type="button" onClick={openNew} style={{ ...buttonStyle, justifySelf: 'start' }}>
                  {t('commerce.addressBook.add')}
                </button>
              )
            )}
          </div>
        )}
      </div>
    </BlockRoot>
  );
}

function AddressForm({
  draft,
  onChange,
  heading,
  busy,
  offerDefault,
  onSubmit,
  onCancel,
}: {
  draft: AddressDraft;
  onChange: (draft: AddressDraft) => void;
  heading: string;
  busy: boolean;
  offerDefault: boolean;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation('site-builder-blocks');
  const headingRef = useRef<HTMLHeadingElement>(null);

  // Opening the form moves focus to it, so a keyboard or screen-reader user is not left on the button.
  useEffect(() => {
    headingRef.current?.focus();
  }, [heading]);

  const field = (name: Exclude<keyof AddressDraft, 'makeDefault'>) => ({
    value: draft[name],
    onChange: (value: string) => onChange({ ...draft, [name]: value }),
  });

  return (
    <form onSubmit={onSubmit} aria-labelledby="address-form-heading" style={formPanelStyle}>
      <h2 id="address-form-heading" ref={headingRef} tabIndex={-1} style={subheadingStyle}>
        {heading}
      </h2>
      <AccountField
        id="address-label"
        label={t('commerce.addressBook.label')}
        hint={t('commerce.addressBook.labelHint')}
        maxLength={100}
        {...field('label')}
      />
      <AccountField
        id="address-name"
        label={t('commerce.addressBook.recipientName')}
        autoComplete="name"
        required
        maxLength={200}
        {...field('recipientName')}
      />
      <AccountField
        id="address-company"
        label={t('commerce.addressBook.company')}
        autoComplete="organization"
        maxLength={200}
        {...field('company')}
      />
      <AccountField
        id="address-line1"
        label={t('commerce.addressBook.line1')}
        autoComplete="address-line1"
        required
        maxLength={200}
        {...field('line1')}
      />
      <AccountField
        id="address-line2"
        label={t('commerce.addressBook.line2')}
        autoComplete="address-line2"
        maxLength={200}
        {...field('line2')}
      />
      <AccountField
        id="address-locality"
        label={t('commerce.addressBook.locality')}
        autoComplete="address-level2"
        required
        maxLength={120}
        {...field('locality')}
      />
      <AccountField
        id="address-region"
        label={t('commerce.addressBook.region')}
        autoComplete="address-level1"
        maxLength={120}
        {...field('region')}
      />
      <AccountField
        id="address-postcode"
        label={t('commerce.addressBook.postcode')}
        autoComplete="postal-code"
        required
        maxLength={20}
        {...field('postcode')}
      />
      <AccountField
        id="address-country"
        label={t('commerce.addressBook.country')}
        hint={t('commerce.addressBook.countryHint')}
        autoComplete="country"
        required
        minLength={2}
        maxLength={2}
        {...field('countryCode')}
      />
      <AccountField
        id="address-phone"
        label={t('commerce.addressBook.phone')}
        type="tel"
        autoComplete="tel"
        maxLength={40}
        {...field('phone')}
      />
      {offerDefault && (
        <label style={checkboxStyle}>
          <input
            type="checkbox"
            checked={draft.makeDefault}
            onChange={(event) => onChange({ ...draft, makeDefault: event.target.checked })}
          />
          <span>{t('commerce.addressBook.makeDefault')}</span>
        </label>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        <button type="submit" disabled={busy} style={buttonStyle}>
          {busy ? t('commerce.account.saving') : t('commerce.addressBook.save')}
        </button>
        <button type="button" onClick={onCancel} disabled={busy} style={secondaryButtonStyle}>
          {t('commerce.addressBook.cancel')}
        </button>
      </div>
    </form>
  );
}

function isSignedOut(failure: unknown): boolean {
  return failure instanceof AccountError && failure.kind === 'NOT_SIGNED_IN';
}

function isDefault(book: AccountAddress[], id: number): boolean {
  return book.some((address) => address.id === id && address.defaultAddress);
}

const backStyle: CSSProperties = {
  display: 'inline-block',
  marginBottom: 12,
  fontSize: 14,
  color: 'var(--color-text-secondary, #6b7280)',
};
const subheadingStyle: CSSProperties = { margin: '0 0 4px', fontSize: 20, fontWeight: 600 };
const listStyle: CSSProperties = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'grid',
  gap: 16,
  gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 260px), 1fr))',
};
const cardStyle: CSSProperties = {
  display: 'grid',
  gap: 12,
  alignContent: 'start',
  padding: 16,
  borderRadius: 'var(--radius-card, 12px)',
  border: '1px solid var(--color-border, #e5e7eb)',
  minWidth: 0,
};
const cardHeadStyle: CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' };
const badgeStyle: CSSProperties = {
  fontSize: 12,
  padding: '2px 8px',
  borderRadius: 999,
  background: 'var(--color-primary-50, #eff6ff)',
  color: 'var(--color-primary-700, #1d4ed8)',
  whiteSpace: 'nowrap',
};
const addressStyle: CSSProperties = { fontStyle: 'normal', lineHeight: 1.5, overflowWrap: 'anywhere' };
const actionsStyle: CSSProperties = { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 };
const linkButtonStyle: CSSProperties = {
  padding: 0,
  border: 'none',
  background: 'none',
  font: 'inherit',
  fontSize: 14,
  color: 'var(--color-primary, #2563eb)',
  textDecoration: 'underline',
  cursor: 'pointer',
};
const dangerLinkStyle: CSSProperties = { ...linkButtonStyle, color: 'var(--color-danger-700, #b91c1c)' };
const formPanelStyle: CSSProperties = {
  display: 'grid',
  gap: 12,
  maxWidth: 520,
  padding: 20,
  borderRadius: 'var(--radius-card, 12px)',
  border: '1px solid var(--color-border, #e5e7eb)',
};
const checkboxStyle: CSSProperties = { display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 };
